import { onUnmounted, toValue, watchEffect, type Component, type MaybeRefOrGetter, type Ref } from 'vue';
import { ArrowLeft, BellOff, BellRing, Check, ExternalLink, Link2, Mail, Plus, Rocket, Send, ShieldCheck } from 'lucide-vue-next';
import { resetMobilePageMenu, useMobilePageMenu } from '~/composables/useMobilePageMenu';
import type { Client, ClientQuickLink } from '~/types/models';

type TabOption = { value: string; label: string; icon: Component };

export function useClientWorkspaceMobileMenu(options: {
    client: MaybeRefOrGetter<Client | null>;
    activeTab: Ref<string>;
    tabOptions: readonly TabOption[];
    tabNotificationCounts: MaybeRefOrGetter<Record<string, number>>;
    notificationsMuted: MaybeRefOrGetter<boolean>;
    quickLinks: MaybeRefOrGetter<ClientQuickLink[]>;
    canMutate: MaybeRefOrGetter<boolean>;
    readyToStartOnboarding: MaybeRefOrGetter<boolean>;
    portalLink: MaybeRefOrGetter<string>;
    sendableFormCount: MaybeRefOrGetter<number>;
    tenantPath: (path: string) => string;
    externalHref: (url: string) => string;
    addProject: () => void;
    startOnboarding: () => void;
    skipOnboarding: () => void;
    manageQuickLinks: () => void;
    sendDigest: () => void;
    viewPortal: () => void;
    copyPortalLink: () => void;
    sendForm: () => void;
    toggleNotifications: () => void;
}) {
    const menu = useMobilePageMenu();

    watchEffect(() => {
        const client = toValue(options.client);
        if (!client) {
            menu.value = { sections: [], actions: [] };
            return;
        }

        menu.value = {
            sections: [
                {
                    id: 'client-sections',
                    label: 'Workspace sections',
                    items: options.tabOptions.map(tab => ({
                        id: tab.value,
                        label: tab.label,
                        icon: tab.icon,
                        badge: toValue(options.tabNotificationCounts)[tab.value] || undefined,
                        active: options.activeTab.value === tab.value,
                        action: () => { options.activeTab.value = tab.value; },
                    })),
                },
                ...(toValue(options.quickLinks).length ? [{
                    id: 'client-quick-links',
                    label: 'Quick links',
                    items: toValue(options.quickLinks).map(link => ({
                        id: `quick-link:${link.id}`,
                        label: link.label,
                        icon: ExternalLink,
                        action: () => navigateTo(options.externalHref(link.url), { external: true }),
                    })),
                }] : []),
            ],
            actions: [
                { id: 'back-to-clients', label: 'Back to CRM', icon: ArrowLeft, action: () => navigateTo(options.tenantPath('/clients')) },
                { id: 'open-inbox', label: 'Open inbox', icon: Mail, action: () => navigateTo(options.tenantPath('/messages')) },
                ...(toValue(options.canMutate) ? [{ id: 'add-project', label: 'Add project', icon: Plus, action: options.addProject }] : []),
                ...(toValue(options.canMutate) && toValue(options.readyToStartOnboarding) ? [
                    { id: 'start-onboarding', label: 'Start onboarding', icon: Rocket, action: options.startOnboarding },
                    { id: 'skip-onboarding', label: 'Skip onboarding', icon: Check, action: options.skipOnboarding },
                ] : []),
                ...(toValue(options.canMutate) ? [{ id: 'manage-quick-links', label: 'Manage quick links', icon: Link2, action: options.manageQuickLinks }] : []),
                { id: 'send-digest', label: 'Send digest', icon: Mail, action: options.sendDigest },
                {
                    id: 'toggle-notifications',
                    label: toValue(options.notificationsMuted) ? 'Unmute client notifications' : 'Mute client notifications',
                    icon: toValue(options.notificationsMuted) ? BellRing : BellOff,
                    action: options.toggleNotifications,
                },
                { id: 'view-portal-as-client', label: 'View portal as client', icon: ShieldCheck, action: options.viewPortal },
                ...(toValue(options.portalLink) ? [{ id: 'copy-portal-link', label: 'Copy portal link', icon: ExternalLink, action: options.copyPortalLink }] : []),
                ...(toValue(options.canMutate) && toValue(options.sendableFormCount) > 0 ? [{
                    id: 'send-form', label: 'Send form', icon: Send, badge: toValue(options.sendableFormCount), action: options.sendForm,
                }] : []),
            ],
        };
    });

    onUnmounted(resetMobilePageMenu);
}
